'use client';
import classNames from 'classnames';
import React from 'react';
import type { EligibilityVerdict } from '../../types/eligibility';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { formatNumberWithSeparator } from '../../utils/helpers/formatters';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { InlineTooltip } from '../InlineTooltip';
import { MaterialIcon } from '../MaterialIcon';
import { ELIGIBILITY_TRANSLATIONS } from './i18n';

export type EligibilityNoteProps = {
  verdict: EligibilityVerdict | undefined;
  className?: string;
};

/**
 * What one loan shows once the reader has entered an income (CMS-1905 UC 2).
 *
 * The falls-short wording always pairs the reason with the fact that they can
 * still apply: this is guidance, and a bare "usually needs more than you earn"
 * would read as a rejection the page has no standing to make.
 *
 * `NONE` renders nothing at all — the lender publishes no criteria we can
 * check, or no income has been entered. Silence is the correct output for
 * both, and they are not distinguishable to the reader.
 */
export const EligibilityNote: React.FC<EligibilityNoteProps> = ({
  verdict,
  className,
}) => {
  // Before the client context is read, not after — the same split
  // `ActiveFilterChips` makes: `useCmsClientContext` throws
  // outside a provider, so reading it first would make a note that renders
  // nothing still require one, and break every bare render of its host block.
  if (!verdict || verdict.outcome === 'NONE') return null;

  return <Note verdict={verdict} className={className} />;
};

/**
 * The wording for one verdict. Shared, so the card's sentence and the table's
 * tooltip cannot drift — they are the same message in two placements.
 *
 * Must be called inside a `CmsClientProvider`; every caller checks the verdict
 * before rendering the component that calls it.
 */
export const useEligibilityMessage = (verdict: EligibilityVerdict): string => {
  const { brand, lang, currencyDisplay } = useCmsClientContext();
  const t = getClientTranslations(ELIGIBILITY_TRANSLATIONS, { brand, lang });

  if (verdict.outcome === 'SHORT_INCOME') {
    return t.eligibility_short_income.replace(
      '{income}',
      `${currencyDisplay}${formatNumberWithSeparator({
        value: verdict.requiredAnnualIncome ?? 0,
        thousandsSeparator: ',',
      })}`,
    );
  }
  return verdict.outcome === 'SHORT_CITIZENSHIP'
    ? t.eligibility_short_citizenship
    : t.eligibility_meets;
};

const Note: React.FC<
  EligibilityNoteProps & { verdict: EligibilityVerdict }
> = ({ verdict, className }) => {
  const meets = verdict.outcome === 'MEETS';
  const text = useEligibilityMessage(verdict);

  return (
    <p
      data-eligibility={verdict.outcome}
      className={classNames(
        'mhg-alpha-typography-caption flex items-start gap-1 text-xs font-semibold leading-tight',
        meets ? 'text-highlights/green-dark' : 'text-highlights/yellow-dark',
        className,
      )}
    >
      {/* Decorative: no `aria-label`, so `MaterialIcon` marks it
          `aria-hidden` and the message is announced as its text alone. */}
      {/* 15px because the Material Symbols CSS pins `line-height: 1`, so the
          glyph's box is exactly its font size, while the text's line box is
          15px (12px at `leading-tight`). Matching the two is what puts the
          glyph on the text's centre — sizing it to the 12px text instead
          leaves it 1.5px high. Measured in the browser, not guessed. */}
      <MaterialIcon
        icon={meets ? 'check' : 'info'}
        px={15}
        className="shrink-0"
      />
      {/* A flex row rather than an inline glyph, so a message that wraps
          aligns under itself instead of under the icon. */}
      <span>{text}</span>
    </p>
  );
};

/**
 * The comparison table's placement of the same verdict (CMS-1905 UC 2).
 *
 * A marker plus a tooltip rather than the sentence: the Apply column is sized
 * for a button, and the full reason wraps to five lines there and dwarfs it.
 * The reason is still reachable — on click, so it works on touch, where a
 * `title` attribute (which is what the prototype uses) would show nothing.
 *
 * Only ever a shortfall: a met criterion adds nothing to the table, so callers
 * pass the verdict only when the reader falls short.
 */
export const EligibilityTableMarker: React.FC<EligibilityNoteProps> = ({
  verdict,
  className,
}) => {
  // Before the client context is read — see the note in `EligibilityNote`.
  if (!verdict || verdict.outcome === 'NONE') return null;

  return <TableMarker verdict={verdict} className={className} />;
};

const TableMarker: React.FC<
  EligibilityNoteProps & { verdict: EligibilityVerdict }
> = ({ verdict, className }) => {
  const { brand, lang } = useCmsClientContext();
  const t = getClientTranslations(ELIGIBILITY_TRANSLATIONS, { brand, lang });
  const reason = useEligibilityMessage(verdict);

  return (
    <span
      data-eligibility={verdict.outcome}
      className={classNames(
        'mhg-alpha-typography-caption text-xs font-semibold text-highlights/yellow-dark',
        className,
      )}
    >
      <InlineTooltip
        // A node, not a string: `InlineTooltip` renders string content through
        // `dangerouslySetInnerHTML`, and this copy is one edit away from
        // carrying an editor-supplied value.
        content={<span>{reason}</span>}
        onOpenEvent="click"
        infoClassName="inline-flex cursor-pointer items-center gap-1"
      >
        <span className="underline">{t.eligibility_check_label}</span>
      </InlineTooltip>
    </span>
  );
};
